import { ProForm, ProFormCaptcha, ProFormText } from '@ant-design/pro-form';
import { Button, Form, message } from 'antd';
import classNames from 'classnames';
import { isEmpty } from 'lodash-es';
import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthContext } from '@/presentation/hooks';
import { AuthInfo, LoginType, Token } from '@/presentation/hooks/user/types';
import { enumTrpc as ProtoUserTrpc } from '@/proto/generated/tpro_rmonitor_product_manager_user';
import { userService } from '@/service/user';
import { isIOTPhone, isPhone } from '@raft/utils/src/validator';
import styles from './index.module.scss';
import CodeType = ProtoUserTrpc.rmonitor.product_manager.CodeType;

export interface RegisterFormProps {
  onSubmit?: () => void;
}

export type SecurityFormValues = {
  phone: string;
  verifyCode: string;
};

type SecurityLocationState = {
  loginType: LoginType;
  userInfo: NonNullable<AuthInfo['userInfo']>;
  token?: Token;
};

export const SecurityForm = (props: RegisterFormProps) => {
  const {} = props;
  const { state } = useLocation();
  const { onLoginSuccess } = useAuthContext();
  const { loginType, userInfo, token } = state as SecurityLocationState;
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [timing, setTiming] = useState(false);

  const onFinish = async (values: SecurityFormValues) => {
    setLoading(true);
    const verifyStatus = await userService.verifyCode({
      type: CodeType.PHONE,
      code: values.verifyCode,
      number: values.phone,
    }, token?.buglyToken);

    if (verifyStatus) {
      onLoginSuccess(loginType, {
        ...userInfo,
        hasPhone: true,
      }, token);
    } else {
      message.error('校验失败，请稍后再试');
    }

    setLoading(false);
  };

  const sendCode = async () => {
    const { phone } = form.getFieldsValue();
    const res = await userService.sendVerifyCode({
      number: phone,
      type: CodeType.PHONE,
    }, token?.buglyToken);

    if (!res) {
      // 抛出错误让验证码组件不倒计时
      throw new Error('');
    }
  };

  return (
    <ProForm<SecurityFormValues>
      form={form}
      style={{ width: 400 }}
      className={styles.registerForm}
      onFinish={onFinish}
      submitter={{
        render: () => <Button
            type={'primary'}
            htmlType={'submit'}
            block
            size={'large'}
            loading={loading}
          >提交</Button>,
      }}
    >
      <ProFormText
        name={'phone'}
        label={''}
        placeholder={'安全手机'}
        help="请填写安全手机号，用于找回帐号、找回密码、帐号保护等场景"
        rules={[
          {
            required: true,
            message: '请填写安全手机号，用于找回帐号、找回密码、帐号保护等场景',
          },
          {
            validator: (_rule, value, callback) => {
              if (isIOTPhone(value)) {
                callback('不能绑定物联网码号');
              }
              if (!isPhone(value)) {
                callback('手机号码格式不正确');
              }

              callback();
            },
          },
        ]}
        fieldProps={{
          addonBefore: '+86',
          className: styles.phoneInput,
        }}
      />
      <ProForm.Item shouldUpdate>
        {(form) => {
          const { phone } = form.getFieldsValue();
          const hasError = form.getFieldError('verifyCode');

          return (
            <ProFormCaptcha
              phoneName={'phone'}
              name={'verifyCode'}
              help={'请输入短信中的6位数验证码'}
              placeholder={'验证码'}
              rules={[
                { required: true, message: '请填写验证码' },
              ]}
              fieldProps={{
                className: styles.captchaInput,
              }}
              captchaProps={{
                className: classNames({
                  [styles.captchaBtn]: true,
                  [styles.captchaBtnWithError]: !isEmpty(hasError),
                  [styles.captchaBtnWithTiming]: timing,
                }),
                disabled: isIOTPhone(phone) || !isPhone(phone) || timing,
                loading: false,
              }}
              countDown={60}
              onGetCaptcha={sendCode}
              captchaTextRender={(timing: boolean, count: number) => {
                setTiming(timing);
                if (!timing) {
                  return '发送验证码';
                }
                return `${count}秒后可重发`;
              }}
            />
          );
        }}
      </ProForm.Item>
    </ProForm>
  );
};
